import React from "react";
import { Box, Typography, Stack, Avatar, Chip } from "@mui/material";
import { AccessTime, CalendarMonth, ArrowForward } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAvatarUrl } from "features/feed/components/Feed/mediaUtils";
import dayjs from "utils/datetime";

const STATUS_CONFIG = {
  confirmed: { color: "#10b981", bg: "rgba(16,185,129,0.1)", border: "rgba(16,185,129,0.2)" },
  pending: { color: "#f59e0b", bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.2)" },
  completed: { color: "#22d3ee", bg: "rgba(59,130,246,0.1)", border: "rgba(59,130,246,0.2)" },
  cancelled: { color: "#6b7280", bg: "rgba(107,114,128,0.1)", border: "rgba(107,114,128,0.2)" },
};

/**
 * Compact session card for dashboards (student + teacher).
 *
 * @param {Object} session - Session object from SessionService
 * @param {"student"|"teacher"} role - Which side is viewing
 * @param {Function} onAction - Click handler for the primary action
 */
const DashboardSessionCard = ({ session, role = "student", onAction }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!session) return null;

  const other = role === "student" ? session.teacher : session.student;
  const otherName = other?.name || t("common.unknown");
  const otherAvatar = getAvatarUrl(other?.avatar?.url || other?.avatar);
  const specialization = other?.specialization || other?.headline || "";

  const scheduledAt = session.scheduledAt ? dayjs(session.scheduledAt) : null;
  const isToday = scheduledAt?.isSame(dayjs(), "day");
  const timeUntil = scheduledAt ? scheduledAt.fromNow() : "";

  const status = session.status || "pending";
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  const price = session.price ? `€${session.price}` : null;
  const duration = session.duration || 60;

  const handleClick = () => {
    if (onAction) {
      onAction(session);
    } else {
      navigate(`/sessions/${session._id}/join`);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        p: 2.5,
        borderRadius: "14px",
        background: "color-mix(in srgb, var(--text-primary) 2.5%, transparent)",
        border: "1px solid color-mix(in srgb, var(--text-primary) 5%, transparent)",
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          background: "color-mix(in srgb, var(--text-primary) 5%, transparent)",
          borderColor: "color-mix(in srgb, var(--text-primary) 10%, transparent)",
          transform: "translateY(-2px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
        },
        flex: 1,
        minWidth: { xs: "100%", sm: 280 },
      }}
    >
      {/* Top: Avatar + Name + Price */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Avatar
          src={otherAvatar}
          sx={{
            width: 40, height: 40,
            background: "color-mix(in srgb, var(--text-primary) 8%, transparent)",
            fontSize: "0.85rem",
            fontWeight: 700,
            color: "color-mix(in srgb, var(--text-primary) 40%, transparent)",
          }}
        >
          {otherName.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: "0.88rem",
            fontWeight: 600,
            color: "var(--text-primary)",
            fontFamily: "'Inter', sans-serif",
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {otherName}
          </Typography>
          {specialization && (
            <Typography sx={{
              fontSize: "0.72rem",
              color: "color-mix(in srgb, var(--text-primary) 40%, transparent)",
              fontFamily: "'Inter', sans-serif",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {specialization}
            </Typography>
          )}
        </Box>
        {price && (
          <Typography sx={{
            fontSize: "0.88rem",
            fontWeight: 700,
            color: "var(--text-primary)",
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            {price}
          </Typography>
        )}
      </Stack>

      {/* Topic */}
      {session.topic && (
        <Typography sx={{
          fontSize: "0.8rem",
          color: "color-mix(in srgb, var(--text-primary) 60%, transparent)",
          fontFamily: "'Inter', sans-serif",
          mb: 1.5,
          lineHeight: 1.4,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {session.topic}
        </Typography>
      )}

      {/* Bottom: Date/Time + Status */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Stack direction="row" spacing={0.4} alignItems="center">
            <CalendarMonth sx={{ fontSize: 13, color: "color-mix(in srgb, var(--text-primary) 30%, transparent)" }} />
            <Typography sx={{
              fontSize: "0.72rem",
              color: "color-mix(in srgb, var(--text-primary) 50%, transparent)",
              fontFamily: "'Inter', sans-serif",
            }}>
              {scheduledAt ? scheduledAt.format("ddd, D MMM") : "—"}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <AccessTime sx={{ fontSize: 13, color: "color-mix(in srgb, var(--text-primary) 30%, transparent)" }} />
            <Typography sx={{
              fontSize: "0.72rem",
              color: "color-mix(in srgb, var(--text-primary) 50%, transparent)",
              fontFamily: "'Inter', sans-serif",
            }}>
              {scheduledAt ? scheduledAt.format("HH:mm") : "—"}
            </Typography>
          </Stack>
        </Stack>

        <Chip
          label={t(`sessions.status.${status}`, status)}
          size="small"
          sx={{
            height: 22,
            fontSize: "0.62rem",
            fontWeight: 700,
            fontFamily: "'Inter', sans-serif",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            background: statusCfg.bg,
            color: statusCfg.color,
            border: `1px solid ${statusCfg.border}`,
            borderRadius: "6px",
          }}
        />
      </Stack>

      {/* Live badge for sessions starting soon */}
      {isToday && status === "confirmed" && scheduledAt && scheduledAt.diff(dayjs(), "hours") <= 2 && scheduledAt.diff(dayjs(), "minutes") > -60 && (
        <Box sx={{
          mt: 1.5,
          py: 0.6,
          borderRadius: "8px",
          background: "rgba(239, 68, 68, 0.08)",
          border: "1px solid rgba(239, 68, 68, 0.15)",
          textAlign: "center",
        }}>
          <Typography sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: "#ef4444",
            fontFamily: "'Inter', sans-serif",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}>
            {scheduledAt.diff(dayjs(), "minutes") > 0
              ? `${t("sessions.live_in")} ${scheduledAt.fromNow(true)}`
              : t("sessions.live_now")
            }
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DashboardSessionCard;
